import React, { MouseEvent, useState, FC } from "react";

import { QueryObserverResult } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  useBoolean,
  useClipboard,
  useApisMembersKaizenIdeasKaizenIdeaRepliesDestroy,
  useApisMembersKaizenIdeasKaizenIdeaRepliesUpdate,
  useEditorState,
  useEditorStateLinkContentGetter,
} from "~/hooks";

import { KaizenIdeaReplyDetail, CommentEditForm } from "~/components/organisms";

import {
  KaizenIdeaReplyType,
  PostDetailMenuItemType,
  AvatarAndNameEmployeeType,
  PreviewWithFileType,
  ApiMemberKaizenIdeasKaizenIdeaRepliesIndexType,
} from "~/domains";

import { Href } from "~/constants/postUrl";

type PropsType = {
  isHighlight?: boolean;
  kaizenIdeaReply: KaizenIdeaReplyType;
  mentions: AvatarAndNameEmployeeType[];
  refetch: () => Promise<
    QueryObserverResult<ApiMemberKaizenIdeasKaizenIdeaRepliesIndexType, unknown>
  >;
};

export const KaizenIdeaReplyDetailWithEditForm: FC<PropsType> = ({
  isHighlight = false,
  kaizenIdeaReply,
  refetch,
  mentions,
}: PropsType) => {
  const { clipboardToCopy } = useClipboard();
  const {
    isChecked: isEditFormOpen,
    setFalse: closeEditForm,
    toggle: toggleEditForm,
  } = useBoolean(false);

  const [editorState] = useEditorState(kaizenIdeaReply.content);
  const [files, setFiles] = useState<PreviewWithFileType[]>([]);
  const { fetchedData } = useEditorStateLinkContentGetter({
    watchEditorState: editorState.value,
    isWatch: isEditFormOpen,
  });

  const { mutate: updateRequest, isLoading } =
    useApisMembersKaizenIdeasKaizenIdeaRepliesUpdate();

  const { mutate: deleteRequest } =
    useApisMembersKaizenIdeasKaizenIdeaRepliesDestroy();

  const deleteKaizenIdeaReplyRequest = () => {
    const isConfirm = confirm("本当に削除しますか？");
    if (!isConfirm) return;

    deleteRequest(
      {
        kaizenIdeaId: kaizenIdeaReply.kaizenIdeaId,
        id: kaizenIdeaReply.id,
      },
      {
        onSuccess: async (data) => {
          toast(data.message);
          await refetch();
        },
      },
    );
  };

  const handleSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const body = {
      content: editorState.jsonContent,
      attachFiles: files,
      quotePostId: fetchedData?.id,
    };
    updateRequest(
      {
        kaizenIdeaId: kaizenIdeaReply.kaizenIdeaId,
        id: kaizenIdeaReply.id,
        body,
      },
      {
        onSuccess: async (data) => {
          toast(data.message);
          await refetch().then(() => {
            closeEditForm();
          });
        },
      },
    );
  };

  const detailItems: PostDetailMenuItemType[] = [
    {
      icon: "ioCopyOutline",
      text: "リンクをコピー",
      onClick: () =>
        clipboardToCopy(
          `${location.origin}${Href.KaizenIdeaReply(kaizenIdeaReply.id)}`,
        ),
      disabled: false,
    },
    {
      icon: "ioCreateOutline",
      text: "編集する",
      onClick: () => toggleEditForm(),
      disabled: !kaizenIdeaReply.editable,
    },
    {
      icon: "ioTrashOutline",
      text: "削除する",
      onClick: () => deleteKaizenIdeaReplyRequest(),
      disabled: !kaizenIdeaReply.editable,
    },
  ];

  return (
    <div className="" id={kaizenIdeaReply.id}>
      {isEditFormOpen ? (
        <CommentEditForm
          submitButton={{
            isSubmitting: isLoading,
            onSubmit: handleSubmit,
          }}
          mentions={mentions}
          post={{
            createdAt: kaizenIdeaReply.createdAt,
            employee: kaizenIdeaReply.employee,
            content: kaizenIdeaReply.content,
          }}
          closeEditForm={closeEditForm}
          files={files}
          setFiles={setFiles}
          editorStateContent={editorState.value}
          setEditorStateContent={editorState.onChange}
          blockQuote={fetchedData}
        />
      ) : (
        <KaizenIdeaReplyDetail
          kaizenIdeaReply={kaizenIdeaReply}
          menuItems={detailItems}
          isHighlight={isHighlight}
        />
      )}
    </div>
  );
};
